import { Edit, ImageNotSupportedRounded } from "@mui/icons-material";
import { Avatar, Badge, Box, Card, CardMedia, IconButton, Rating, Stack, styled, Typography } from "@mui/material";
import React from "react";
import { useNavigate } from "react-router-dom";
import Slider from "react-slick";
import { useStore } from "../../../store/useStore";
import { tokens } from "../../../store/tokens";
import "./ServiceCardItem.css";

const DEFAULT_IMAGE = "//aps.songjukti.com/image/web_admin/data_entry.jpg";

const getSafeImageUrl = (rawUrl) => {
  if (!rawUrl || typeof rawUrl !== "string") return null;
  let url = rawUrl.trim();
  if (!url) return null;

  if (url.startsWith("//")) {
    url = "https:" + url;
  } else if (!/^https?:\/\//i.test(url)) {
    url = "https://" + url.replace(/^\/+/, "");
  }

  const match = url.match(/^(https?:\/\/)(.*)$/i);
  if (!match) return null;
  const [, protocol, rest] = match;
  const finalUrl = protocol + rest.replace(/\/{2,}/g, "/");

  try {
    // eslint-disable-next-line no-new
    new URL(finalUrl);
    return finalUrl;
  } catch (e) {
    return null;
  }
};

const pickText = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (Array.isArray(value)) return value[0]?.title ?? "";
  if (typeof value === "object") return value.title ?? "";
  return String(value);
};

const buildMainImageUrl = (service_pic) => {
  if (typeof service_pic === "string" && service_pic.trim()) return service_pic;
  if (Array.isArray(service_pic) && service_pic[0]?.pic_url) {
    return "//yeapbe.com/" + service_pic[0].pic_url;
  }
  return DEFAULT_IMAGE;
};

const extractExtraPicUrl = (entry) => {
  if (!entry) return null;
  if (typeof entry === "string") return entry;
  return entry.mpic || entry.pic_url || entry.service_pic || entry.image || null;
};

const formatPrice = (price, currency) => {
  const isNegotiable = price == null || price === "" || price === "0" || price === 0 || price === "1" || price === 1;
  if (isNegotiable) return "Negotiate";
  return `${currency || "BDT"} ${price}`;
};

const NoImageBanner = ({ height = "20vh" }) => (
  <Box
    sx={{
      height,
      width: "100%",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      bgcolor: tokens.color.surfaceMuted,
      color: tokens.color.inkFaint,
      gap: 0.5,
    }}
  >
    <ImageNotSupportedRounded sx={{ fontSize: "1.75rem" }} />
    <Typography variant="caption" sx={{ fontWeight: 600 }}>
      No image
    </Typography>
  </Box>
);

const ServiceCardItem = (props) => {
  const navigate = useNavigate();
  const { user } = useStore();
  const { data, setRender, handleUpServiceOpen, editOff } = props;
  const {
    poster,
    agency_info,
    poster_info,
    poster_pic,
    service_category,
    service_info,
    service_pic,
    service_pic_list,
    service_price,
    service_refer,
    service_review,
    offer_st,
    user_state,
    service_currency,
    service_review_quantity,
    service_subcategory,
    service_title,
  } = data;

  const sliderSettings = {
    dots: false,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 6000,
  };

  const mainImage = getSafeImageUrl(buildMainImageUrl(service_pic));
  const extraImages =
    service_pic_list?.map((element) => getSafeImageUrl(extractExtraPicUrl(element))).filter(Boolean) || [];
  const hasMultipleImages = extraImages.length > 1;
  const allImages = hasMultipleImages ? [mainImage, ...extraImages].filter(Boolean) : [mainImage].filter(Boolean);

  const isOwner = Boolean(user?.sopnoid) && user.sopnoid === poster;
  const rating = typeof service_review === "object" ? Number(service_review?.average_rating) || 0 : Number(service_review) || 0;
  const reviewCount = typeof service_review === "object" ? service_review?.total_review : service_review_quantity;
  const isOnline = user_state?.[0]?.states === 1;

  // Was hardcoded to "//ap.songjukti.com/..." (missing the "s" in "aps"
  // that every other fallback in this codebase uses), so this particular
  // fallback avatar silently 404'd. Reuses the same DEFAULT_IMAGE constant
  // as everything else instead of a second, slightly-wrong copy of it.
  const avatarSrc =
    getSafeImageUrl(
      agency_info?.agency_logo?.[0]
        ? "//yeapbe.com/" + agency_info.agency_logo[0].pic_url
        : poster_info?.user_pic?.[0]
        ? "//yeapbe.com/" + poster_info.user_pic[0].pic_url
        : typeof poster_pic === "string" && poster_pic
        ? poster_pic
        : DEFAULT_IMAGE
    ) || DEFAULT_IMAGE;

  const goToDetails = () => {
    navigate("/servicedetails/" + service_refer);
    setRender?.(service_refer);
  };

  return (
    <Card
      elevation={0}
      sx={{
        width: "100%",
        borderRadius: `${tokens.card.radius}px`,
        border: `1px solid ${tokens.color.line}`,
        overflow: "hidden",
        transition: "box-shadow 0.15s ease",
        "&:hover": { boxShadow: tokens.shadow.md },
      }}
    >
      <Box sx={{ position: "relative" }}>
        {allImages.length === 0 ? (
          <Box onClick={goToDetails} sx={{ cursor: "pointer" }}>
            <NoImageBanner />
          </Box>
        ) : hasMultipleImages ? (
          <Slider {...sliderSettings}>
            {allImages.map((imgUrl, index) => (
              <Box key={imgUrl + index} sx={{ height: "20vh" }}>
                <CardMedia component="img" height="100%" image={imgUrl} alt={service_title} sx={{ cursor: "pointer" }} onClick={goToDetails} />
              </Box>
            ))}
          </Slider>
        ) : (
          <Box sx={{ height: "20vh" }}>
            <CardMedia component="img" height="100%" image={allImages[0]} alt={service_title} sx={{ cursor: "pointer" }} onClick={goToDetails} />
          </Box>
        )}

        <Box sx={{ position: "absolute", top: 10, right: 10, px: 1, py: 0.25, borderRadius: `${tokens.radius.sm}px`, bgcolor: "rgba(18,36,46,0.75)" }}>
          <Typography variant="caption" sx={{ color: tokens.color.white, fontWeight: 700 }}>
            {formatPrice(service_price, service_currency)}
          </Typography>
        </Box>
      </Box>

      <Stack sx={{ p: 2, pt: 4, position: "relative" }} spacing={0.75}>
        <StyledBadge
          overlap="circular"
          badgeContent=""
          variant="dot"
          sx={{
            position: "absolute",
            top: -24,
            left: 16,
            "& .MuiBadge-badge": { backgroundColor: isOnline ? tokens.color.success : tokens.color.inkFaint },
          }}
        >
          <Avatar alt="Provider" src={avatarSrc} sx={{ width: 48, height: 48, border: `3px solid ${tokens.color.surface}` }} />
        </StyledBadge>

        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="body2" sx={{ fontWeight: 600, color: tokens.color.teal }} onClick={goToDetails}>
            {pickText(service_category)}
            {pickText(service_subcategory) && ` · ${pickText(service_subcategory).slice(0, 12)}`}
          </Typography>
          {!editOff && isOwner && (
            <IconButton aria-label="edit" size="small" onClick={() => handleUpServiceOpen(service_refer, offer_st)}>
              <Edit fontSize="inherit" sx={{ color: tokens.color.inkMuted }} />
            </IconButton>
          )}
        </Stack>

        <Typography
          variant="subtitle1"
          sx={{ color: tokens.color.ink, minHeight: "2.6em", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}
          onClick={goToDetails}
        >
          {service_title}
        </Typography>

        <Stack direction="row" spacing={0.5} alignItems="center">
          <Rating name="read-only" value={rating} precision={0.5} readOnly size="small" />
          <Typography variant="caption" sx={{ color: tokens.color.inkMuted }}>
            {rating} ({reviewCount ?? 0})
          </Typography>
        </Stack>
      </Stack>
    </Card>
  );
};

export default ServiceCardItem;

const StyledBadge = styled(Badge)(() => ({
  "& .MuiBadge-badge": {
    right: 9,
    top: 11,
    border: `3px solid ${tokens.color.surface}`,
    padding: 5,
    borderRadius: "50%",
    // `color="grey"` on the Badge itself isn't a valid MUI palette key —
    // that crashes the same way the profile badge did earlier. This
    // override is the only thing that should set the dot's color.
  },
}));
